import React, { useState } from 'react'
import dateFormat from 'dateformat'

import { DisplayTypeWGIcon } from '@sainsburys-tech/bolt-icons'
import { Modal, ModalHeader, ModalContent, ProgressBar, Ratings as StarRating } from '@sainsburys-tech/bolt'
import { InView } from 'react-intersection-observer'
import MediaGallery from '../MediaGallery/MediaGallery'

import { BadgesList } from '../UserProfile/UserProfile'
import BazaarVoiceHelpfulnessContainer from '../BazaarVoiceHelpfulness/BazaarVoiceHelpfulnessContainer'
import config from '../../config/bazaarvoice.json'
import { pushEventAndTrack } from '../../helpers/taggingSerializer'

import * as Styled from './ReviewItem.style'

const getUserProfileText = profile => {
  const { name, contextAttributes, location } = profile
  const { Age } = contextAttributes
  return `${name}${Age ? `, ${Age.replace('to', '-')}` : ''}${location ? `, ${location}` : ''}`
}

const CrossReview = ({ productReviewed: { id, name }, partNumber, syndication }) => {
  const BRAND = process.env.BRAND || 'ARGOS'
  const isTuBrand = BRAND === 'TU'
  const isTuOnTu = isTuBrand && syndication?.name === 'tu.co.uk'

  if ((partNumber === id && !syndication) || (isTuOnTu && partNumber === id)) return null

  if (syndication && !isTuOnTu) {
    return (
      <Styled.CrossReview data-test='review-item-cross-review-visiting'>
        Written by a customer while visiting{' '}
        <a href={syndication.url} target='_blank' rel='noreferrer'>
          {syndication.name}
        </a>
      </Styled.CrossReview>
    )
  }

  return (
    <Styled.CrossReview data-test='review-item-cross-review-from'>
      This review is from: <a href={`/product/${id}`}>{name}</a>
    </Styled.CrossReview>
  )
}

const ClientResponse = ({ clientResponses }) => {
  const submittedDate = dateFormat(new Date(clientResponses?.Date).toUTCString(), 'dd mmmm yyyy')

  return (
    <Styled.ClientResponseContainer data-test='reviews-accordion-reviews-response'>
      <Styled.ClientResponseTitle>
        <Styled.ResponseTeamText>Response from {clientResponses.SourceClientName}:</Styled.ResponseTeamText>
        <Styled.Date>{submittedDate}</Styled.Date>
      </Styled.ClientResponseTitle>
      <Styled.ResponseTeamText dangerouslySetInnerHTML={{ __html: clientResponses.Response }} />
      <Styled.ResponseTeamRegards>
        {clientResponses.Name}, {clientResponses.SourceClientName} customer service
      </Styled.ResponseTeamRegards>
    </Styled.ClientResponseContainer>
  )
}

const AdditionalData = ({ contextAttributes }) => {
  const contextItems = Object.keys(contextAttributes).map((label, index) => {
    if (label !== 'Age' && label !== 'Gender')
      return (
        <Styled.AdditionalData key={index}>
          <strong>{label}</strong> {contextAttributes[label]}
        </Styled.AdditionalData>
      )
    return null
  })

  return <Styled.AdditionalDataContainer>{contextItems}</Styled.AdditionalDataContainer>
}

const FitToText = fit => {
  switch (fit) {
    case 1:
      return 'Too small'
    case 2:
      return 'A little small'
    case 3:
      return 'True to size'
    case 4:
      return 'A little big'
    case 5:
      return 'Too big'
    default:
      return ''
  }
}

const ReviewItem = ({ review, partNumber, dataTest = 'review-item' }) => {
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [loadReviewImages, setLoadReviewImages] = useState(false)
  const { attributes, id } = review
  const {
    productReviewed,
    submitted,
    overallRating,
    isSyndicated,
    syndication,
    title,
    text,
    helpfulness,
    profile,
    photos,
    secondaryRatings
  } = attributes
  let { clientResponses, totalClientResponseCount } = attributes

  const { badges } = profile
  const badgeUrls = config.REVIEWS.BADGE_IMAGE_URLS
  const badgeText = config.REVIEWS.BADGE_TESTER_TEXT
  const submittedDate = new Date(submitted)
  const submittedTimeStamp = new Date(submittedDate - submittedDate.getTimezoneOffset() * 60000).toISOString()

  const images = photos ? photos.map(item => item.Sizes.normal.Url) : []
  const media = { images }
  const displayImages = media.images.length // && showLoadImages

  if (clientResponses) {
    clientResponses = clientResponses.filter(response => response.Department === 'Internal Support Team')
    totalClientResponseCount = clientResponses.length
  }

  const fitToEnd = rating1 => {
    return rating1.key === 'Fit' ? 1 : -1
  }

  const alphabetical = (itemA, itemB) => {
    return itemA.key < itemB.key ? -1 : itemA.key > itemB.key // A < B return -1 | A > B return 1 | return 0
  }

  const handleReviewImgClick = () => {
    setImageModalOpen(true)

    pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_reviewsPhotoClick',
        eventAction: 'PDP Reviews - Photo Click'
      },
      attributes: {
        starRating: overallRating
      }
    })
  }

  return (
    <div data-test={`${dataTest}`}>
      <Styled.Title>{title}</Styled.Title>
      <Styled.SubTitle>
        <StarRating
          large
          message={<Styled.RatingMessage>{overallRating}</Styled.RatingMessage>}
          position='left'
          rating={overallRating}
          itemProp='reviewRating'
          itemType='https://schema.org/Rating'
          itemScope
          data-test='review-item-ratings'
        />
        <Styled.Date dateTime={submittedTimeStamp}>{submitted}</Styled.Date>
      </Styled.SubTitle>
      <Styled.SecondaryAttributeContainer>
        {Object.keys(secondaryRatings)
          .map((value, key) => {
            if (value === 'Fit')
              return (
                <Styled.SecondaryAttributeWrapper key={value} data-test={`${dataTest}-subattributes-${key}`}>
                  <span>{value}</span>
                  <Styled.FitText key={`fitText-${key}`} data-test={`${dataTest}-subattributes-fit-${key}`}>
                    {FitToText(secondaryRatings[value])}
                  </Styled.FitText>
                </Styled.SecondaryAttributeWrapper>
              )
            return (
              <Styled.SecondaryAttributeWrapper key={value} data-test={`${dataTest}-subattributes-${key}`}>
                <span>{value}</span>
                <ProgressBar
                  value={secondaryRatings[value]}
                  scale={5}
                  numberOfSegments={5}
                  showCount={false}
                  data-test={`${dataTest}-subattributes-progress-bar-${key}`}
                />
              </Styled.SecondaryAttributeWrapper>
            )
          })
          .sort(alphabetical)
          .sort(fitToEnd)}
      </Styled.SecondaryAttributeContainer>
      <AdditionalData contextAttributes={profile.contextAttributes} />
      <p itemProp='reviewBody' dangerouslySetInnerHTML={{ __html: text }} />
      {displayImages ? (
        <InView triggerOnce rootMargin='0px 0px 200px 0px' onChange={inView => inView && setLoadReviewImages(true)}>
          {loadReviewImages && (
            <div className='xs-row'>
              <Styled.ReviewsImage onClick={handleReviewImgClick} data-test='review-image'>
                <Styled.ReviewsImageIcon>
                  <DisplayTypeWGIcon />
                  <span>{media.images.length}</span>
                </Styled.ReviewsImageIcon>
                <img itemProp='image' src={media.images[0]} width='140px' height='140px' alt='reviews' />
              </Styled.ReviewsImage>
              <Modal show={imageModalOpen} onClose={() => setImageModalOpen(false)} height='auto'>
                <ModalHeader>{`Photos by ${profile ? profile.name : 'unknown'}`}</ModalHeader>
                <ModalContent>
                  <span>
                    <MediaGallery {...{ media }} isPortrait={false} isReviews />
                  </span>
                  <h4 className='font-title--small'>{title}</h4>
                  <StarRating
                    large
                    message={overallRating}
                    position='left'
                    rating={overallRating}
                    itemProp='reviewRating'
                    itemType='https://schema.org/Rating'
                    itemScope
                    data-test='review-item-ratings'
                  />
                  <p itemProp='reviewBody' dangerouslySetInnerHTML={{ __html: text }} />
                </ModalContent>
              </Modal>
            </div>
          )}
        </InView>
      ) : null}
      <BadgesList {...{ badges, badgeUrls, badgeText, isSyndicated }} />
      <Styled.UserProfile>{getUserProfileText(profile)}</Styled.UserProfile>
      <CrossReview {...{ productReviewed, partNumber, syndication }} />
      {totalClientResponseCount > 0 && clientResponses.length > 0 && (
        <ClientResponse clientResponses={clientResponses[0]} />
      )}
      {helpfulness && (
        <BazaarVoiceHelpfulnessContainer
          helpfulCount={helpfulness.helpful}
          unhelpfulCount={helpfulness.unhelpful}
          contentType='review'
          contentId={id}
          starRating={overallRating}
        />
      )}
    </div>
  )
}

export default ReviewItem
